'use client';

import { ComponentPropsWithRef, useState } from 'react';
import {
  localizedLinkStyles,
  localizedLinkWrapperStyles,
  underlineStyles,
} from '@/common/components/LocalizedLink/LocalizedLink.styled';
import { useParams, usePathname } from 'next/navigation';

import { Box } from '@mui/material';
import Link from 'next/link';
import Underline from './Underline';

export interface LocalizedLinkProps extends ComponentPropsWithRef<'a'> {
  locale?: string;
  brushUnderline?: boolean;
  isNavLink?: boolean;
  onSamePageClick?: () => void;
}

const LocalizedLink = ({
  href,
  children,
  style,
  ref,
  locale,
  brushUnderline = false,
  isNavLink = false,
  onSamePageClick,
  ...props
}: LocalizedLinkProps) => {
  const { lang } = useParams();
  const pathname = usePathname();
  const [isHovering, setIsHovering] = useState(false);

  if (!href) {
    console.error('LocalizedLink: href is required');
    return null;
  }

  const withTrailingSlash =
    !href.endsWith('/') && !href.includes('?') ? `${href}/` : href;

  const localizedHref = `/${locale ?? lang}${withTrailingSlash}`;
  const isExternal =
    href.startsWith('http') ||
    href.startsWith('mailto') ||
    href.startsWith('tel');

  const isActive = isNavLink && pathname === localizedHref;
  const showUnderline = brushUnderline && isActive;

  const handleClick = (e: React.MouseEvent) => {
    if (isActive && onSamePageClick) {
      e.preventDefault();
      onSamePageClick();
    }
  };

  const content = (
    <Box sx={localizedLinkWrapperStyles}>
      <span>{children}</span>
      {(isHovering && brushUnderline) || showUnderline ? (
        <Underline sx={underlineStyles({ isHovering, isActive })} />
      ) : null}
    </Box>
  );

  return isExternal ? (
    <a
      onMouseOver={() => setIsHovering(true)}
      onMouseOut={() => setIsHovering(false)}
      onClick={handleClick}
      ref={ref}
      rel="noopener noreferrer"
      target="_blank"
      href={href}
      {...props}
      style={{ ...localizedLinkStyles, ...style }}
    >
      {content}
    </a>
  ) : (
    <Link
      onMouseOver={() => setIsHovering(true)}
      onMouseOut={() => setIsHovering(false)}
      onClick={handleClick}
      ref={ref}
      href={localizedHref}
      {...props}
      style={{ ...localizedLinkStyles, ...style }}
    >
      {content}
    </Link>
  );
};

export default LocalizedLink;
